import { RootState } from "@store/root-reducer"
import { createReducer, createSelector } from "@reduxjs/toolkit"
import { pixelToCm, pixelToInches } from "@utils/data"
import { PageOption } from "@store/reducers/init/types"
import { SCHEDULE_TEMPLATE_ID } from "@eventdex/builder/src/Consts"
import { isScheduleTemplate } from "@eventdex/builder/src/utils/printing/utils"

export const getAllFormatSizes = (state: RootState) => state.paper.formats

//page data
export const getAllPaperSizes = (state: RootState) => state.paper.pageSizes
export const getIsCustomPageSizeMode = (state: RootState) => state.paper.isCustomPageSizeMode
export const getPageData = (state: RootState) => state.paper.pageData

export const getSelectedPage = (state: RootState) => state.paper.selectedPage
export const getSelectedPageData = createSelector(getPageData, getSelectedPage, (pageData, selectedPage) => {
  return pageData[selectedPage] || {}
})

//3rd PartySettings
export const getThirdPartySettings = (state: RootState) => state.paper.thirdParty
export const getThirdPartyFormat = (state: RootState) => state.paper.thirdPartyFormat
export const getThirdPartyName = (state: RootState) => state.paper.thirdPartyName
//FormatSettings
export const getPageOption = (state: RootState) => state.paper.pageOption
export const getSeperatePages = (state: RootState) => state.paper.seperatePages
export const getThirdPartyConfig = createSelector(
  getThirdPartySettings,
  getThirdPartyFormat,
  getPageOption,
  getSeperatePages,
  getPageData,
  (thirdPartyEnabled, thirdPartyConfig, pageOption, separatePages, pageData) => {
    if (thirdPartyEnabled && thirdPartyConfig) {
      if (pageOption === PageOption.MULTI && !separatePages) {
        let multiplier = pageData.length
        return {
          ...thirdPartyConfig,
          Paper_Height_MM__c: thirdPartyConfig.Paper_Height_MM__c * multiplier,
          Paper_Height_Inch: thirdPartyConfig.Paper_Height_Inch * multiplier,
          Paper_Height__c: thirdPartyConfig.Paper_Height__c * multiplier,
          Number_Of_Row_In_a_Sheet__c: thirdPartyConfig.Number_Of_Row_In_a_Sheet__c * multiplier,
        }
      }
      return thirdPartyConfig
    }
    return null
  }
)


export const getThirdPartyPaperName = (state: RootState) => state.paper.thirdPartyFormat?.Paper_Name__c


export const getCurrentDesignId = (state: RootState) => state.paper.currentDesignId
export const getCurrentDesignSelected = createSelector(
  [getCurrentDesignId, (state) => state.ed_builder.VisualDesign.entities],
  (id, entities) => {
    return entities[id] || {}
  }
)
export const getIsScheduleTemplate = createSelector(getCurrentDesignSelected, (template) => {
  return isScheduleTemplate(template)
})

export const getRawBadgeSize = (state: RootState) => state.paper.selectedFormat
export const getSelectedFormat = createSelector(
  getThirdPartyConfig,
  getRawBadgeSize,
  (state, ignoreRotation = false) => ignoreRotation,
  (thirdPartyConfig, rawBadgeSize, ignoreRotation = false) => {
    return formatSize(rawBadgeSize, thirdPartyConfig, "Badge", ignoreRotation)
  }
)

function formatSize(size, paperTemplate, type, ignoreRotation = false) {
  let mmHeight, mmWidth, inchHeight, inchWidth, pxWidth, pxHeight

  //Rounding the sizes sometimes create issue on the printer due to 1px difference.
  //that's why we have to use values from the paper template if it's defined.
  if (paperTemplate) {
    mmWidth = paperTemplate[type + "_Width_MM__c"]
    inchWidth = paperTemplate[type + "_Width_Inch"]
    pxWidth = paperTemplate[type + "_Width__c"]

    //For continuous paper let user decide the size.
    if (!paperTemplate.isContinuous) {
      mmHeight = paperTemplate[type + "_Height_MM__c"]
      inchHeight = paperTemplate[type + "_Height_Inch"]
      pxHeight = paperTemplate[type + "_Height__c"]
    }
  }

  size = {
    ...size,
    width: pxWidth || size.width,
    height: pxHeight || size.height,
    inches: {
      width: inchWidth || pixelToInches(size.width),
      height: inchHeight || pixelToInches(size.height),
    },
    mm: {
      width: mmWidth || pixelToCm(size.width) * 10,
      height: mmHeight || pixelToCm(size.height) * 10,
    },
  }

  //if canvas is rotated, swap the width and height
  if (paperTemplate?.Rotate_Canvas__c && !ignoreRotation) {
    return {
      ...size,
      width: size.height,
      height: size.width,
      inches: {
        width: size.inches.height,
        height: size.inches.width,
      },
      mm: {
        width: size.mm.height,
        height: size.mm.width,
      },
    }
  }
  return size
}

//FormatSettings
export const getRawPaperSize = (state: RootState) => state.paper.pageSize
export const getPaperSize = createSelector(
  getThirdPartyConfig,
  getRawPaperSize,
  getRawBadgeSize,
  (state, ignoreRotation = false) => ignoreRotation,
  (thirdPartyConfig, rawPaperSize, rawBadgeSize, ignoreRotation) => {
    let size = formatSize(rawPaperSize, thirdPartyConfig, "Paper", ignoreRotation)

    if (thirdPartyConfig?.isContinuous) {
      let badgeSize = formatSize(rawBadgeSize, thirdPartyConfig, "Badge", ignoreRotation)
      size.height = badgeSize.height
      size.width = badgeSize.width
      size.inches = badgeSize.inches
      size.mm = badgeSize.mm
    }
    return size
  }
)

export const getIsCustomFormatSizeMode = (state: RootState) => state.paper.isCustomFormatSizeMode
export const getUnitType = (state: RootState) => state.paper.unitType
export const getEditType = (state: RootState) => state.paper.editType
export const getTemplateType = (state: RootState) => state.paper.templateType

//Layout
export const getLayoutSettings = createSelector(
  getThirdPartyConfig,
  (state: RootState) => state.paper.layout,
  getPaperSize,
  (thirdPartyConfig, layout, paperSize) => {
    let settings = { ...layout, gridSizes: layout.gridSizes.custom || layout.gridSizes.calculated, ...paperSize }

    //Override the custom layout settings with the third party settings from the server
    if (thirdPartyConfig) {
      //gridSizes
      if (thirdPartyConfig.Number_Of_Column_In_a_Sheet__c) {
        settings.gridSizes = {
          columns: thirdPartyConfig.Number_Of_Column_In_a_Sheet__c,
          rows: thirdPartyConfig.Number_Of_Row_In_a_Sheet__c,
        }
      }

      //badgeMargins
      if (thirdPartyConfig.horizontalSpacing || thirdPartyConfig.verticalSpacing) {
        settings.badgeMargins = {
          right: thirdPartyConfig.horizontalSpacing || 0,
          bottom: thirdPartyConfig.verticalSpacing || 0,
        }
      }

      //pageMargins
      if (thirdPartyConfig.Offset_From_Top__c) {
        settings.pageMargins = {
          top: thirdPartyConfig.Offset_From_Top__c,
          left: thirdPartyConfig.Offset_From_Left__c,
          right: thirdPartyConfig.Offset_From_Right__c,
          bottom: thirdPartyConfig.Offset_From_Bottom__c,
        }
      }
      //paperSize
      if (thirdPartyConfig.Paper_Width__c || thirdPartyConfig.Paper_Height__c) {
        settings.paperSize = {
          width: thirdPartyConfig.Paper_Width__c,
          height: thirdPartyConfig.Paper_Height__c,
        }
        //if it is continuous, then we need to set the paper size to the badge size
        if (thirdPartyConfig.isContinuous) {
        }

        //Display Canvas in landscape mode.
        if (thirdPartyConfig.Rotate_Canvas__c) {
          settings.paperSize = {
            width: thirdPartyConfig.Paper_Height__c,
            height: thirdPartyConfig.Paper_Width__c,
          }
        }
      }
    }
    return settings
  }
)

export const getLayoutType = createSelector(getLayoutSettings, (layout) => layout.type)
export const getPaperMargins = createSelector(getLayoutSettings, (layout) => layout.pageMargins)
export const getBadgeMargins = createSelector(getLayoutSettings, (layout) => layout.badgeMargins)
export const getLayoutPaperSize = createSelector(getLayoutSettings, (layout) => layout.paperSize)
export const getGridSize = createSelector(getLayoutSettings, (layout) => layout.gridSizes)

//ModuleEnabled
export const getEnabledModules = (state: RootState) => state.paper.enabledModules

//needConfirmation
export const getNeedConfirmation = (state: RootState) => state.paper.needConfirmation

export const getCanvasResetCounter = (state: RootState) => state.paper.canvasReset

export const selectPaperInfo = createSelector(
  getThirdPartySettings,
  getThirdPartyConfig,
  getThirdPartyName,
  getPageOption,
  getPageData,
  (state) => getSelectedFormat(state, true),
  (state: RootState) => state.paper.layout,
  (state: RootState) => state.paper.seperatePages,
  (isThirdParty, thirdParty, thirdPartyName, pageOption, pageData, badgeSize, layout, seperatePages) => {
    let infoString = `${badgeSize.inches.width}" x ${badgeSize.inches.height}"`

    if (layout.type === "multi") {
      infoString += " Custom"
    }

    if (isThirdParty) {
      if (!thirdParty) {
        return thirdPartyName || ""
      }

      const { Product__c, Template__c, Number_of_Badge_Per_Page__c, isContinuous } = thirdParty
      //Add the product and template name
      infoString += ` - ${Product__c} ${Template__c} Badge`

      //Add continuous
      if (isContinuous) {
        infoString += " Continuous"
      }

      // Add the number of badges per page
      // if (Number_of_Badge_Per_Page__c) {
      //   infoString += `- ${Number_of_Badge_Per_Page__c} Badges`
      // }
    }

    //TODO: what should we display here?
    //Add the pages option
    if (pageOption === "double") {
      infoString += " Double-Sided"
      if (pageData[pageData.length - 1]?.printRotation) {
        infoString += `, Mirror`
      }
    }

    if (pageOption === "multi") {
      infoString += `, ${pageData.length}`
      infoString += " Multi Pages"
      if (seperatePages) {
        infoString += ", Separate"
      }
    }

    //Mirror or no mirror show also display

    return infoString
  }
)

export const getGridUpdateCounter = (state: RootState) => state.paper.gridUpdateCounter
export const getIsAddOnBadge = (state: RootState) => state.paper.isAddOnBadge
export const getSelectedTags = (state: RootState) => state.paper.selectedTags

//floormap
export const getIsFloormap = (state: RootState) => state.paper.isFloorMap
export const getIsAttendeeFloormap = (state: RootState) => state.paper.isAttendeeFloorMap
export const getShowbackgroundImage = (state: RootState) => state.paper.showBackgroundImage
export const getboothColorSetting = (state: RootState) => state.paper.boothColorSetting
export const getAttendees = (state: RootState) => state.paper.attendees
export const getTableDataGlobal = (state: RootState) => state.paper.tableDataGlobal
export const getIsTableFinder = (state: RootState) => state.paper.isTableFinder
export const getSelectedPageIndex = (state: RootState) => state.paper.selectedPage
export const getSponsorLogos = (state:RootState) => state.paper.sponsorLogos
export const getShowBoothRegistration = (state: RootState) => state.paper.showBoothRegistration